import { phoneNumberSchema } from "@/components/Forms/schema/phoneNumberSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "phoneNumber",
    attributesSchema: phoneNumberSchema,
});

const phoneNumbersSelector = createDataSelector(resourceSelector);

export type PhoneNumbers = ReturnType<typeof phoneNumbersSelector>;
export type MrtPhoneNumber = PhoneNumbers[number];

type Props = {
    companyUUID: string;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtPhoneNumbersForCompany = ({
    companyUUID,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<PhoneNumbers> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: [
            "company-phone-number-table-data",
            companyUUID,
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        queryFn: async () => {
            const fetchURL = apiUrl("/company/phone-number/for-company");

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    companyUUID: companyUUID,
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: columnFilters ?? [],
                    globalFilter: globalFilter ?? "",
                    sorting: sorting ?? [],
                }),
            });

            return await response.json();
        },
        select: phoneNumbersSelector,
    });
};
